import { ExternalLink, Navigation } from "lucide-react";
import { useState } from "react";

import { OsmMap } from "@/components/site/OsmMap";
import { SeedMark } from "@/lib/brand";
import type { Locale } from "@/lib/i18n";
import { cn } from "@/lib/utils";

export const SCHOOL_COORDS = { lat: 36.45995, lng: -5.04781 };
export const SCHOOL_MAP_QUERY = encodeURIComponent(
  "Swedish International School, Urb Costalita 1, Cancelada, Estepona",
);
export const SCHOOL_MAP_ADDRESS = "Urb Costalita 1, Cancelada · 29680 Estepona";
export const SCHOOL_MAP_EXTERNAL = `https://www.google.com/maps/search/?api=1&query=${SCHOOL_MAP_QUERY}`;
export const SCHOOL_MAP_DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${SCHOOL_MAP_QUERY}`;


const COPY: Record<Locale, { title: string; open: string; directions: string; place: string }> = {
  sv: {
    title: "Karta till Swedish International School i Estepona",
    open: "Öppna i Google Maps",
    directions: "Vägbeskrivning",
    place: "Cancelada, Estepona",
  },
  en: {
    title: "Map to Swedish International School in Estepona",
    open: "Open in Google Maps",
    directions: "Get directions",
    place: "Cancelada, Estepona",
  },
  es: {
    title: "Mapa hasta Swedish International School en Estepona",
    open: "Abrir en Google Maps",
    directions: "Cómo llegar",
    place: "Cancelada, Estepona",
  },
};

/**
 * Delad kartvy med skolans egen markör och en mjuk grön overlay.
 * Overlayen är klickfri (pointer-events-none) förutom markörkortet, och
 * täcker aldrig Googles logotyp eller villkorslänkar i nederkant.
 */
export function SchoolMap({
  locale,
  className,
  mapClassName = "h-[360px] lg:h-[520px]",
  compact = false,
}: {
  locale: Locale;
  className?: string;
  mapClassName?: string;
  compact?: boolean;
}) {
  const t = COPY[locale] ?? COPY.sv;
  const [markerEl, setMarkerEl] = useState<HTMLDivElement | null>(null);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-border bg-card shadow-soft",
        className,
      )}
    >
      <div className="relative isolate">
        <OsmMap
          lat={SCHOOL_COORDS.lat}
          lng={SCHOOL_COORDS.lng}
          title={t.title}
          markerEl={markerEl}
          className={cn("block w-full", mapClassName)}
        />

        {/* Mjuk grön ton så kartan matchar sajtens färger. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-b from-primary/12 via-transparent to-primary/10 mix-blend-multiply dark:opacity-40 dark:mix-blend-normal"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[2] rounded-xl ring-1 ring-inset ring-primary/15"
        />

        {/* Skolmarkör – monteras i kartan via MapLibre Marker. */}
        <div ref={setMarkerEl}>
          <div
            className={cn(
              "relative flex items-center gap-2 rounded-full border border-border bg-card/95 shadow-soft backdrop-blur",
              compact ? "px-2 py-1" : "px-3 py-2",
            )}
          >
            <span
              className={cn(
                "flex items-center justify-center rounded-full bg-primary/10",
                compact ? "size-6" : "size-8",
              )}
            >
              <SeedMark className={compact ? "size-4" : "size-5"} />
            </span>
            <span className="leading-tight">
              <span
                className={cn(
                  "block font-semibold text-foreground",
                  compact ? "text-[0.68rem]" : "text-xs",
                )}
              >
                Swedish International School
              </span>
              <span
                className={cn(
                  "block text-muted-foreground",
                  compact ? "text-[0.6rem]" : "text-[0.7rem]",
                )}
              >
                {t.place}
              </span>
            </span>
            {/* Pilspets nedåt som pekar på platsen. */}
            <span
              aria-hidden
              className="absolute left-1/2 top-full size-3 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b border-r border-border bg-card"
            />
          </div>
        </div>
      </div>


      <div
        className={cn(
          "flex flex-wrap items-center justify-between gap-3 border-t border-border bg-card",
          compact ? "px-3 py-2" : "px-5 py-4",
        )}
      >
        <p className={cn("text-muted-foreground", compact ? "text-xs" : "text-sm")}>
          {SCHOOL_MAP_ADDRESS}
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            href={SCHOOL_MAP_EXTERNAL}
            target="_blank"
            rel="noreferrer"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md bg-primary/10 font-medium text-primary transition-colors hover:bg-primary/20",
              compact ? "px-2.5 py-1.5 text-xs" : "px-3.5 py-2 text-sm",
            )}
          >
            <ExternalLink className={compact ? "size-3.5" : "size-4"} />
            {t.open}
          </a>
          <a
            href={SCHOOL_MAP_DIRECTIONS}
            target="_blank"
            rel="noreferrer"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border border-border font-medium text-foreground transition-colors hover:bg-sand",
              compact ? "px-2.5 py-1.5 text-xs" : "px-3.5 py-2 text-sm",
            )}
          >
            <Navigation className={compact ? "size-3.5" : "size-4"} />
            {t.directions}
          </a>
        </div>
      </div>
    </div>
  );
}
